// @ts-strict-ignore
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Log } from '@shared/debug/log';
import { Matrix4, Mesh, Object3D, Plane, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { MetricUtils } from '@3d-scene/common/util/metric-utils';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Line } from '@3d-scene/model/line';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { Face3D } from '@3d-scene/model/face-3d';
import { Injectable } from '@angular/core';
import { ProjectModel } from '@project/model/project.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { UserModel } from '@user/model/user.model';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { AttachedArticleContainer } from '@project/components/article/service/article-container-attached.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { IsThreeJsType } from '../common/util/is-threejs-type';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ContainerUtils } from './container.utils';

@Injectable({ providedIn: 'root' })
export class ContainerService {
  constructor(
    private projectModel: ProjectModel,
    private globalContainer: GlobalContainer
  ) {}

  public getFirstArticleContainerInGlobal(): ArticleContainer {
    return this.findArticleContainerInGlobal(ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject).id);
  }

  public findCompositionContainersInGlobal(): CompositionContainer[] {
    const compositionContainers: CompositionContainer[] = this.globalContainer.children.filter(
      (object3d) => ContainerUtils.isCompositionContainer(object3d),
    );
    return compositionContainers;
  }

  public findArticleContainerInGlobal(articleUsageGuid: string): ArticleContainer | undefined {
    if (!articleUsageGuid) return null;
    const compositionContainers = this.findCompositionContainersInGlobal();
    return compositionContainers
      .flatMap(composition => composition.children)
      .find(object3d => ContainerUtils.isArticleContainer(object3d) && object3d.articleGUID === articleUsageGuid) as ArticleContainer || null;
  }

  public async attachArticleContainer(data: AttachedArticleContainer) {
    let compositionContainer = this.findCompositionContainerInGlobal(data.compositionId);
    if (!compositionContainer) {
      Log.error('Could not find composition container by guid: ' + data.compositionId);
    }
    await compositionContainer?.attachArticleContainerHandler(data);
  }

  public enumerateArticleContainersInGlobal(): ArticleContainer[] {
    return this.globalContainer.children.flatMap(container =>
      [container, ...container.children].filter((object3d) => ContainerUtils.isArticleContainer(object3d))
    );
  }

  public enumerateArticleContainersInGlobalExcept(excludedGUIDs: string[]): ArticleContainer[] {
    return this.enumerateArticleContainersInGlobal().filter(c => !excludedGUIDs.includes(c.articleGUID));
  }

  /** @returns true if the article is a label that sits directly on a label adapter */
  public isLabelOnAdapter(container: ArticleContainer): boolean {
    if (container.article.type.id != ArticleTypeIds.LABELING) return false;
    const parentId = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
    if (!parentId) return false;

    return this.findArticleContainerInGlobal(parentId)?.article.type.id == ArticleTypeIds.MARKER_CARRIER
  }

  public findArticleContainersInGlobalByGuids(guids: string[]): ArticleContainer[] {
    if (ArrayUtils.isEmpty(guids)) {
      return [];
    }
    const articleContainers: ArticleContainer[] = [];
    for (const articleGUID of guids) {
      const article: ArticleContainer = this.findArticleContainerInGlobal(articleGUID);
      if (article) {
        articleContainers.push(article);
      }
    }
    return articleContainers;
  }

  /**
   * Method searches for all articles, which are connected to the provided accepting area.
   * @param acceptingAreaId id of the accepting area
   * @return array of ArticleContainers
   *
   */
  public getConnectedArticleContainersByAcceptingAreaId(acceptingAreaId: MetaElementUsageIdentityVO): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    const articles: ArticleContainer[] = this.enumerateArticleContainersInGlobal();
    for (const articleContainer of articles) {
      if (articleContainer.findGivingArea(acceptingAreaId)) {
        result.push(articleContainer);
      }
    }
    return result;
  }

  public getConnectedArticleContainers(articleContainerOrUsage: ArticleContainer | ArticleUsageVO): ArticleContainer[] {
    if (!articleContainerOrUsage) {
      return [];
    }
    const articleUsage = ContainerUtils.isArticleContainer(articleContainerOrUsage) ? articleContainerOrUsage.articleUsage : articleContainerOrUsage;
    const acceptingAreaIds = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    const articleContainers = acceptingAreaIds
      .map(id => this.findArticleContainerInGlobal(id))
      .filter(Boolean);
    return articleContainers;
  }

  public getConnectedArticleContainersRecursively(articleContainer: ArticleContainer): ArticleContainer[] {
    return this.getConnectedArticleContainers(articleContainer)
      .flatMap(container => [container, ...this.getConnectedArticleContainersRecursively(container)]);
  }

  public findFacesOnPlane(figure: Object3D, plane: Plane, excludedMeshes: Mesh[] = null, epsilon: number = 0.16, matrix: Matrix4 = null): Face3D[] {
    const facesMap: Face3D[] = this.extractFaces(figure, excludedMeshes);
    const result: Face3D[] = [];
    facesMap.forEach((data: Face3D) => {
      const vertices: Vector3[] = [data.vertices[0].clone(), data.vertices[1].clone(), data.vertices[2].clone()];
      if (matrix) {
        vertices[0].applyMatrix4(matrix);
        vertices[1].applyMatrix4(matrix);
        vertices[2].applyMatrix4(matrix);
      }
      if (this.faceOnPlane(vertices, plane, epsilon)) {
        result.push(data);
      }
    });
    return result;
  }

  public extractFaces(object3D: Object3D, excludedMeshes: Mesh[] = null): Face3D[] {
    if (!object3D) {
      return [];
    }
    const objects3D: Object3D[] = Object3DUtils.getChildrenRecursively(object3D);
    objects3D.push(object3D);
    const result: Face3D[] = [];
    for (const object of objects3D) {
      if (IsThreeJsType.mesh(object) && (!excludedMeshes || !excludedMeshes.find((value: Object3D) => value.name === object.name))) {
        this.addFaceToVerticesMapping(object, result);
      }
    }
    return result;
  }

  /**
   * Returns dictionary <Face to Face vertices [v0, v1, v2]>
   * @param mesh
   * @param result
   */
  private addFaceToVerticesMapping(mesh: Mesh, result: Face3D[]): void {
    const faces: Face3D[] = FaceUtils.getFaces(mesh);
    for (const face of faces) {
      result.push(face);
    }
  }

  /**
   * Check if face is lying in the plane
   * @param faceVertices face
   * @param plane plane
   * @param epsilon
   * @return true, iff is lying in the plane
   *
   */
  public faceOnPlane(faceVertices: Vector3[], plane: Plane, epsilon: number = 0.16): boolean {
    return (
      Math.abs(plane.distanceToPoint(faceVertices[0])) < epsilon &&
      Math.abs(plane.distanceToPoint(faceVertices[1])) < epsilon &&
      Math.abs(plane.distanceToPoint(faceVertices[2])) < epsilon
    );
  }

  public calculateBaseLine(acceptingConnection: Connection, acceptingTransform: Matrix4, inverseTransform: boolean = false): AdvancedLine {
    const clonedRectangle: Rectangle3D = acceptingConnection.localRectangle.clone();
    if (acceptingTransform) {
      clonedRectangle.transform(acceptingTransform);
    }

    if (this.isInverseTransformAvailable(inverseTransform, clonedRectangle)) {
      return new AdvancedLine(clonedRectangle.point3, clonedRectangle.point2);
    } else {
      return new AdvancedLine(clonedRectangle.point1, clonedRectangle.point4);
    }
  }

  private isInverseTransformAvailable(inverseTransform: boolean, rectangle: Rectangle3D): boolean {
    return inverseTransform && MathUtils.compare(rectangle.point1.x, rectangle.point4.x) < 0;
  }

  /**
   * Separates given lines by splitter line.
   */
  public splitLines(lines: AdvancedLine[], splitter: AdvancedLine, mergeLines: boolean = true): AdvancedLine[] {
    const result: AdvancedLine[] = [];
    for (const line of lines) {
      if (line.hasLine(splitter)) {
        // full line is on the baseLine
        if (!MathUtils.equalsNumber3D(line.start, splitter.start) || !mergeLines /*add empty lines*/) {
          result.push(
            new AdvancedLine(
              line.start,
              splitter.start,
              new OverlapInfo(line.overlapTypes, line.overlapLeft, splitter.overlapLeft, line.overlapLeftTypes, splitter.overlapRightTypes)
            )
          );
        }
        if (!MathUtils.equalsNumber3D(splitter.end, line.end) || !mergeLines /*add empty lines*/) {
          result.push(
            new AdvancedLine(
              splitter.end,
              line.end,
              new OverlapInfo(line.overlapTypes, splitter.overlapRight, line.overlapRight, splitter.overlapLeftTypes, line.overlapRightTypes)
            )
          );
        }
      } else if (line.hasPoint(splitter.start)) {
        // base line contains only beginning part (start) of the line
        if (!MathUtils.equalsNumber3D(line.start, splitter.start) || !mergeLines /*add empty lines*/) {
          result.push(
            new AdvancedLine(
              line.start,
              splitter.start,
              new OverlapInfo(line.overlapTypes, line.overlapLeft, splitter.overlapLeft, line.overlapLeftTypes, splitter.overlapRightTypes)
            )
          );
        }
      } else if (line.hasPoint(splitter.end)) {
        // base line contains only end part of the line
        if (!MathUtils.equalsNumber3D(splitter.end, line.end) || !mergeLines /*add empty lines*/) {
          result.push(
            new AdvancedLine(
              splitter.end,
              line.end,
              new OverlapInfo(line.overlapTypes, splitter.overlapRight, line.overlapRight, splitter.overlapLeftTypes, line.overlapRightTypes)
            )
          );
        }
      } else if (!splitter.hasLine(line)) {
        // base line has no splitter points
        result.push(line);
      }
    }
    return result;
  }

  /**
   * Provides line (point range) which defines space on connection line used by current article.
   * @param articleContainer article container
   * @param acceptingArea accepting area usage
   * @param baseLine accepting area connection line (top line of connection rectangle, between points #1 and #2)
   * @return line or null if connection area doesn't contain the article
   *
   */
  public findFigureLine(articleContainer: ArticleContainer, acceptingArea: ConnectionAreaUsageVO, baseLine: Line): AdvancedLine {
    const translationMatrix: Matrix4 = this.getArticleTransformationMatrix(articleContainer);

    let closestPoint: Vector3 = null;
    let farestPoint: Vector3 = null;
    const sideFaces: Face3D[] = this.findConnectionSideFaces(articleContainer, acceptingArea);
    for (const face of sideFaces) {
      for (let i: number = 0; i < 3; i++) {
        const v: Vector3 = face.vertices[i];
        let position: Vector3 = new Vector3(v.x, v.y, v.z);
        position = MathUtils.transform(position, translationMatrix);

        const pointOnLine: Vector3 = MathUtils.closestPointOnLine(baseLine.start, baseLine.end, position);
        if (!closestPoint) {
          closestPoint = pointOnLine;
          farestPoint = pointOnLine;
        } else {
          if (baseLine.start.distanceTo(closestPoint) > baseLine.start.distanceTo(pointOnLine)) {
            closestPoint = pointOnLine;
          }
          if (baseLine.end.distanceTo(pointOnLine) < baseLine.end.distanceTo(farestPoint)) {
            farestPoint = pointOnLine;
          }
        }
      }
    }

    let articleLine: AdvancedLine = null;
    if (closestPoint) {
      articleLine = AdvancedLine.createInstanceByArticle(articleContainer.article, closestPoint, farestPoint);
      if (ArticleUsageUtils.isArticleRotated(articleContainer.articleUsage)) {
        articleLine.reverseOverlapProperties();
      }
    }
    return articleLine;
  }

  /**
   * Method checks all faces of article container and returns only those, which are in the same plane with connection mesh.
   * @param articleContainer article container
   * @param acceptingArea accepting area usage
   * @return array of faces
   */
  public findConnectionSideFaces(articleContainer: ArticleContainer, acceptingArea: ConnectionAreaUsageVO): Face3D[] {
    if (!acceptingArea) {
      return [];
    }
    const givingArea: MetaElementUsageIdentityVO = articleContainer.findGivingArea(acceptingArea.identity);
    if (!givingArea) {
      return [];
    }
    const cube: Connection = articleContainer.findFirstConnectionByName(givingArea.metaElementName);
    if (!cube) {
      return [];
    }
    const plane: Plane = new Plane();
    const connectionRectangle: Rectangle3D = cube.localRectangle;
    plane.setFromCoplanarPoints(connectionRectangle.point1, connectionRectangle.point2, connectionRectangle.point3);
    const faces: Face3D[] = this.findFacesOnPlane(articleContainer.model, plane);
    return faces;
  }

  /**
   * Provides transformation matrix, which defines article container position and rotation.
   * @param articleContainer article container
   * @return matrix resulting matrix
   */
  public getArticleTransformationMatrix(articleContainer: ArticleContainer): Matrix4 {
    if (!articleContainer) {
      return null;
    }
    articleContainer.updateMatrix();
    return articleContainer.matrix.clone();
  }

  /**
   * Provide composition container by article container
   * @param articleContainer article container
   * @return composition container
   *
   */
  public getCompositionContainer(articleContainer: ArticleContainer): CompositionContainer {
    if (!articleContainer) {
      return null;
    }
    return articleContainer.parent as CompositionContainer;
  }

  /**
   * Provide article container by connection cube
   * @param connection connection cube
   * @return article container
   *
   */
  public getArticleContainer(connection: Connection): ArticleContainer {
    if (!connection) {
      return null;
    }
    return connection.articleContainer;
  }

  /**
   * Update availability of connection cube according to its connection state.
   * If articles are already connected by this connection, then cube will be disabled.
   * @param connection connection
   */
  public updateConnectionAvailability(connection: Connection): void {
    const isMetaInfoShown: boolean = connection.isMetaInfoShown();
    if (connection.metaElementUsage) {
      if (connection.metaElementUsage instanceof ConnectionAreaUsageVO) {
        let enabled: boolean = isMetaInfoShown;
        if (enabled) {
          if (
            connection.metaElementUsage.connection.acceptingAreaId.equals(connection.metaElementUsage.identity) &&
            connection.metaElementVO instanceof ConnectionAreaVO
          ) {
            // article connected and is accepting
            enabled = ConnectionAreaUsageUtils.isFreeConnectionUsage(
              connection.metaElementUsage,
              connection.metaElementVO.connectionMultiplicity
            );
          } else {
            for (const id of (connection.metaElementUsage as ConnectionAreaUsageVO).connection.givingAreaIds) {
              if (id.equals(connection.metaElementUsage.identity)) {
                // article connected and is giving
                enabled = false;
                break;
              }
            }
          }
        }
        connection.updateMouseEnabled(enabled);
      }
    } else {
      connection.updateMouseEnabled(isMetaInfoShown);
    }
  }

  public findCompositionContainerInGlobal(compositionId: string): CompositionContainer {
    if (!compositionId) {
      return null;
    }
    for (const childContainer of Object3DUtils.getChildrenRecursively(this.globalContainer)) {
      if (ContainerUtils.isCompositionContainer(childContainer) && childContainer.compositionGuid === compositionId) {
        return childContainer;
      }
    }
    return null;
  }

  public convertLineToRectangle(line: Line, baseRectangle: Rectangle3D, inverseTransform: boolean = false): Rectangle3D {
    const rect: Rectangle3D = new Rectangle3D();
    rect.point1 = line.start;
    rect.point4 = line.end;
    if (this.isInverseTransformAvailable(inverseTransform, baseRectangle)) {
      rect.point3 = MathUtils.closestPointOnLine(baseRectangle.point4, baseRectangle.point1, line.end);
      rect.point2 = MathUtils.closestPointOnLine(baseRectangle.point4, baseRectangle.point1, line.start);
    } else {
      rect.point3 = MathUtils.closestPointOnLine(baseRectangle.point2, baseRectangle.point3, line.end);
      rect.point2 = MathUtils.closestPointOnLine(baseRectangle.point2, baseRectangle.point3, line.start);
    }
    return rect;
  }

  /**
   * Finds the distance to each of the four points of the rectangle and returns the minimum distance.
   * @param rect Rectangle3D
   * @param point Vector3D
   * @return distance as Number
   */
  public findMinDistanceToRectanglePoints(rect: Rectangle3D, point: Vector3): number {
    const points: Vector3[] = rect.toArray();
    let minDistance: number = Number.MAX_VALUE;
    let distance: number;
    for (const p of points) {
      distance = p.distanceTo(point);
      if (minDistance > distance) {
        minDistance = distance;
      }
    }
    return minDistance;
  }

  public resizeConnection(connection: Connection, connectionWidth: number, connectionHeight: number): void {
    const scaleMatrix: Matrix4 = this.getScaleMatrix(connection, connectionWidth, connectionHeight);

    FaceUtils.scaleMesh(connection.mesh, MetricUtils.getMiddlePoint(connection.mesh), scaleMatrix);
  }

  private getScaleMatrix(connection: Connection, connectionWidth: number, connectionHeight: number): Matrix4 {
    const connectionRectangle: Rectangle3D = connection.localRectangle;
    const widthRatio: number = connectionWidth / connectionRectangle.width;
    const heightRatio: number = connectionHeight / connectionRectangle.height;

    const scaleMatrix: Matrix4 = MathUtils.constructBasicScaleMatrix(
      connectionRectangle.point1,
      connectionRectangle.point4,
      MathUtils.getInterpolated(connectionRectangle.point4, connectionRectangle.point1, widthRatio),
      true
    );
    scaleMatrix.multiply(
      MathUtils.constructBasicScaleMatrix(
        connectionRectangle.point1,
        connectionRectangle.point2,
        MathUtils.getInterpolated(connectionRectangle.point2, connectionRectangle.point1, heightRatio),
        true
      )
    );

    return scaleMatrix;
  }

  public getConnectionsFromOneLocationGroup(articleContainer: ArticleContainer, rectangleByConnection: Rectangle3D): Connection[] {
    const connectionCubesFromOneLocationGroup: Connection[] = [];
    const connectionAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(articleContainer.article, ConnectionAreaVO.SIDE_ACCEPTING);

    for (const connectionArea of connectionAreas) {
      const currentConnection: Connection = articleContainer.findFirstConnectionByName(connectionArea.name);
      if (!currentConnection) {
        continue;
      }
      const currentRectangle: Rectangle3D = currentConnection.compositionRectangle;
      const middlePointOfConnection: Vector3 = MathUtils.calculateMiddlePointByRectangle(currentRectangle);
      if (MathUtils.isPointInsideRectangle(rectangleByConnection, middlePointOfConnection)) {
        connectionCubesFromOneLocationGroup.push(currentConnection);
      }
    }
    return connectionCubesFromOneLocationGroup;
  }

  /**
   * Method changes sequence of sourceRect points if UV-direction (p1 - p2, p1 - p4) if sourceRect and targetRect do not match.
   */
  public changeRectangleDirection(sourceRect: Rectangle3D, targetRect: Rectangle3D): Rectangle3D {
    const points: Vector3[] = sourceRect.toArray();
    return this.compareRectanglesDirection(sourceRect, targetRect)
      ? Rectangle3D.fromArray(points)
      : Rectangle3D.fromArray([points[3], points[0], points[1], points[2]]);
  }

  public compareRectanglesDirection(rect1: Rectangle3D, rect2: Rectangle3D): boolean {
    let v1: Vector3 = rect1.point2.clone().sub(rect1.point1);
    v1 = new Vector3(Math.abs(v1.x), Math.abs(v1.y), Math.abs(v1.z));
    let v2: Vector3 = rect2.point2.clone().sub(rect2.point1);
    v2 = new Vector3(Math.abs(v2.x), Math.abs(v2.y), Math.abs(v2.z));

    if (
      (MathUtils.compare(v1.x, v2.x) === 0 && MathUtils.compare(v1.y, v2.y) === 0) ||
      (MathUtils.compare(v1.x, v2.x) === 0 && MathUtils.compare(v1.z, v2.z) === 0) ||
      (MathUtils.compare(v1.y, v2.y) === 0 && MathUtils.compare(v1.z, v2.z) === 0)
    ) {
      return true;
    } else {
      return false;
    }
  }

  public resizeConnectionFromStart(connection: Connection, connectionWidth: number, connectionHeight: number): void {
    const clonedMesh: Mesh = MeshUtils.cloneMesh(connection.mesh);
    clonedMesh.name = connection.id;
    clonedMesh.material = connection.mesh.material;
    connection.setMesh(clonedMesh);

    const scaleMatrix: Matrix4 = this.getScaleMatrix(connection, connectionWidth, connectionHeight);
    const startPoint: Vector3 = this.getStartPoint(connection.mesh);
    FaceUtils.scaleMesh(connection.mesh, startPoint, scaleMatrix);
  }

  private getStartPoint(object: Object3D): Vector3 {
    const min: Vector3 = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    const max: Vector3 = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    MetricUtils.findMixMaxRecursevly(object, min, max);
    return new Vector3(max.x, (min.y + max.y) / 2, (min.z + max.z) / 2);
  }

  /**
   * Returns relative offset of startPoint by first point of connection line.
   */
  public calculateConnectionOffset(connectionLine: Line, startPoint: Vector3, excludingArticleIds: string[] = null): number {
    const firstConnectionPoint: Vector3 = MathUtils.closestPointOnLine(connectionLine.start, connectionLine.end, startPoint);
    let offset: number = 0;

    if (connectionLine.hasPoint(firstConnectionPoint)) {
      offset = connectionLine.start.distanceTo(startPoint);
    } else if (firstConnectionPoint.distanceTo(connectionLine.start) < firstConnectionPoint.distanceTo(connectionLine.end)) {
      offset = -connectionLine.start.distanceTo(startPoint);
    }
    /* TODO: (from flex) drilling holes might be handled specially in the future:
    else {
      offset = Vector3D.distance(connectionLine.start, startPoint);
    }*/
    return offset;
  }

  public getBackwardConnectedArticleUsageIdsWithComposite(articleUsage: ArticleUsageVO): string[] {
    return ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage).map((id) => {
      const containerParent = this.findArticleContainerInGlobal(id);
      if (!ArticleUsageUtils.isCompositePartArticleUsage(containerParent.articleUsage)) {
        return id;
      }
      return CompositeArticleInfoUtils.getCompositeArticleUsageId(containerParent);
    });
  }

  public isDeletable(articleUsageIds: string[], projectMetaElementService: ProjectMetaElementService, userModel: UserModel): boolean {
    if (userModel.user.hasPermission("MAINTAIN_ARTICLES")) {
      return true;
    }
    articleUsageIds = this.collectForwardConnectedArticleUsageIds(articleUsageIds);
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.findArticleContainerInGlobal(articleUsageId);
      if (articleContainer) {
        const parentAcceptingAreaIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
          articleContainer.articleUsage
        );
        for (const parentAcceptingAreaId of parentAcceptingAreaIds) {
          const metaElement: MetaElementVO = projectMetaElementService.getMetaElementByUsage(
            parentAcceptingAreaId.articleUsageId,
            parentAcceptingAreaId.metaElementName
          );
          if (
            ArrayUtils.getItemIndex(parentAcceptingAreaId.articleUsageId, articleUsageIds) === -1 &&
            MetaElementUtils.isConnectedArticleNotDeletable(metaElement)
          ) {
            return false;
          }
        }
      }
    }
    return true;
  }

  public collectForwardConnectedArticleUsageIds(articleUsageIds: string[], result: string[] = null): string[] {
    if (ArrayUtils.isEmpty(result)) {
      result = [];
    }

    for (const articleUsageId of articleUsageIds) {
      if (result.indexOf(articleUsageId) !== -1) {
        continue;
      }
      result.push(articleUsageId);
      const articleContainer: ArticleContainer = this.findArticleContainerInGlobal(articleUsageId);
      if (!articleContainer) {
        continue;
      }
      const connectedArticleUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleContainer.articleUsage);
      this.collectForwardConnectedArticleUsageIds(connectedArticleUsageIds, result);
    }

    return result;
  }

  public getCompositePartArticles(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): ArticleContainer[] {
    const compositePartArticleUsageIds: string[] = this.getCompositePartArticleUsageIds(compositePartArticle, useStrippedCompositeInfo);
    return this.findArticleContainersInGlobalByGuids(compositePartArticleUsageIds);
  }

  public getCompositePartArticleUsageIds(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): string[] {
    const result: string[] = [];
    for (const articleUsage of this.getCompositePartArticleUsages(compositePartArticle, useStrippedCompositeInfo)) {
      result.push(articleUsage.id);
    }
    return result;
  }

  public getCompositePartArticleUsages(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): ArticleUsageVO[] {
    const compositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(compositePartArticle, useStrippedCompositeInfo);
    if (!compositeArticleUsageId) {
      return [];
    }
    return this.projectModel.getCompositePartArticleUsagesById(compositeArticleUsageId, useStrippedCompositeInfo);
  }

  public getBaseCompositePartArticles(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    const compositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(compositePartArticle, useStrippedCompositeInfo);
    const compositePartArticleContainers: ArticleContainer[] = this.getCompositePartArticles(compositePartArticle, useStrippedCompositeInfo);
    for (const compositePartArticleContainer of compositePartArticleContainers) {
      const baseArticles: ArticleContainer[] = this.getBaseCompositePartArticlesRecursively(
        [compositePartArticleContainer],
        compositeArticleUsageId,
        useStrippedCompositeInfo
      );
      for (const baseArticle of baseArticles) {
        if (result.indexOf(baseArticle) < 0) {
          result.push(baseArticle);
        }
      }
    }

    return result;
  }

  private getBaseCompositePartArticlesRecursively(
    compositePartArticles: ArticleContainer[],
    compositeArticleUsageId: string,
    useStrippedCompositeInfo: boolean = false
  ): ArticleContainer[] {
    const result: ArticleContainer[] = compositePartArticles;
    const compositePartArticle: ArticleContainer = compositePartArticles[0];
    const parentArticleGUIDs: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(compositePartArticle.articleUsage);
    if (parentArticleGUIDs.length === 0) {
      return result;
    }

    const parentArticle: ArticleContainer = this.findArticleContainerInGlobal(parentArticleGUIDs[0]);
    if (!parentArticle || CompositeArticleInfoUtils.getCompositeArticleUsageId(parentArticle, useStrippedCompositeInfo) !== compositeArticleUsageId) {
      return result;
    }

    return this.getBaseCompositePartArticlesRecursively(
      this.findArticleContainersInGlobalByGuids(parentArticleGUIDs),
      compositeArticleUsageId,
      useStrippedCompositeInfo
    );
  }

  public getParentArticleContainersByConnection(connection: Connection): ArticleContainer[] {
    const parentArticleUsageIds = MetaElementUsageUtils.backwardConnectedArticleUsageIds(connection.metaElementUsage);
    return this.getParentArticleContainersByArticleUsageIds(parentArticleUsageIds);
  }

  private getParentArticleContainersByArticleUsageIds(articleUsageIds: string[]) {
    const articleContainers = this.findArticleContainersInGlobalByGuids(articleUsageIds);
    return articleContainers.flatMap((articleContainer) => [
      articleContainer,
      ...this.getParentArticleContainersByArticleUsageIds(ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage)),
    ]);
  }

  public findParentAcceptingArticleContainers(articleContainer: ArticleContainer) {
    const backwardConnectedArticleUsageIds = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage);
    return this.findArticleContainersInGlobalByGuids(backwardConnectedArticleUsageIds);
  }

  public getParentContainersConnectedThroughChildren(articleContainer: ArticleContainer): Set<ArticleContainer> {
    const articleContainers = new Set<ArticleContainer>();
    let parentsToCheck = [articleContainer];
    do {
      for (const parentToCheck of parentsToCheck) {
        articleContainers.add(parentToCheck);
      }
      const children = ArrayUtils.unique(parentsToCheck.flatMap((parent) => this.getConnectedArticleContainers(parent)));
      const parents = ArrayUtils.unique(children.flatMap((child) => this.findParentAcceptingArticleContainers(child)));
      const newParents = parents.filter((parent) => !articleContainers.has(parent));
      parentsToCheck = newParents;
    } while (parentsToCheck.length > 0);
    return articleContainers;
  }
}
